import {
  InputHTMLAttributes,
  ReactElement,
  forwardRef,
  isValidElement,
  memo,
  useEffect,
  useRef,
  useState,
} from 'react'
import { AnimatePresence, m } from 'framer-motion'

import Input, { BasicInputProps } from '@/components/GenericInput'
import GenericOption, { OptionsType } from '@/components/GenericOption'
import { useOnClickOutside } from '@/lib/hooks/useOnClickOutside'
import { cn } from '@/lib/misc/Utils'
import InputSearch from '@/components/InputSearch'
import ChevronDownIcon from '@icons/chevron-down'
import CloseIcon from '@icons/x-mark'

interface SelectInputProps
  extends
    Omit<BasicInputProps, 'value'>,
    Omit<InputHTMLAttributes<HTMLInputElement>, 'value'> {
  options: OptionsType[]
  setValue: (val: any, option?: OptionsType) => void
  onReset?: () => void
  onClear?: () => void
  icon?: ReactElement
  isSmall?: boolean
  suffix?: ReactElement
  value?: string | number | readonly string[] | ReactElement
  isOpen?: boolean
  enableSearchField?: boolean
}

interface GenericSelectProps extends SelectInputProps {
  inputType?: 'standard' | 'search'
}

const GenericSelect = forwardRef<HTMLInputElement, GenericSelectProps>(
  (
    {
      cy,
      className,
      options,
      inputType = 'standard',
      setValue,
      onReset,
      onClear,
      icon,
      isSmall,
      isOpen,
      enableSearchField = false,
      ...props
    },
    ref
  ) => {
    const [isOpened, setIsOpened] = useState(false)
    const [dropdownSearchTerm, setDropdownSearchTerm] = useState('')
    const containerRef = useRef(null)
    const searchInputRef = useRef<HTMLInputElement>(null)

    useOnClickOutside(containerRef, () => {
      setIsOpened(false)
      setDropdownSearchTerm('') // Clear search when closing dropdown
    })

    useEffect(() => {
      if (isOpen !== undefined) {
        setIsOpened(isOpen)
      }
    }, [isOpen])

    useEffect(() => {
      if (isOpened && enableSearchField && searchInputRef.current) {
        setTimeout(() => {
          searchInputRef.current?.focus()
        }, 10)
      }
    }, [isOpened, enableSearchField])

    const filteredOptions =
      enableSearchField && dropdownSearchTerm
        ? options?.filter(
            (option) =>
              option.label
                .toLowerCase()
                .includes(dropdownSearchTerm.toLowerCase()) ||
              option.formattedLabel
                ?.toString()
                .toLowerCase()
                .includes(dropdownSearchTerm.toLowerCase())
          )
        : options

    const onSelectOptionHandler = (val: any, option?: OptionsType) => {
      setValue(val, option)
      setIsOpened(false)
      setDropdownSearchTerm('')
    }

    const onClearHandler = () => {
      if (onClear) {
        onClear()
      }
    }

    return (
      <div className={cn('relative', className)} ref={containerRef}>
        {inputType === 'standard' ? (
          props.readOnly && isValidElement(props.value) ? (
            <div
              className={cn(
                'h-full w-full px-3 border bg-white rounded-lg border-solid border-neutral-20 hover:border-neutral-40 cursor-default flex items-center',
                isSmall ? 'py-2' : 'py-3'
              )}
              onClick={() => setIsOpened(!isOpened)}
              data-cy={cy}
            >
              {props.value}
            </div>
          ) : (
            <Input
              {...props}
              value={
                typeof props.value === 'string' ||
                typeof props.value === 'number' ||
                Array.isArray(props.value)
                  ? props.value
                  : undefined
              }
              cy={cy}
              ref={ref}
              onFocus={() => setIsOpened(true)}
              onClick={() => setIsOpened(true)}
              onChangeCapture={() => setIsOpened(true)}
              autoComplete="off"
              style={props.readOnly ? { cursor: 'default' } : {}}
              icon={icon}
              isSmall={isSmall}
            />
          )
        ) : (
          <InputSearch
            {...props}
            value={
              typeof props.value === 'string' ||
              typeof props.value === 'number' ||
              Array.isArray(props.value)
                ? props.value
                : undefined
            }
            cy={cy}
            ref={ref}
            showSearchIcon={!options?.length}
            onFocus={() => setIsOpened(true)}
            onClick={() => setIsOpened(true)}
            onChangeCapture={() => setIsOpened(true)}
            onReset={() => {
              if (onReset) {
                onReset()
              }
            }}
          />
        )}
        {!!options?.length && (inputType !== 'search' || !props.value) && (
          <div className="absolute -translate-y-2/4 right-3 top-2/4 z-20 flex items-center gap-1">
            {props.value && onClear && (
              <CloseIcon
                className="text-neutral-40 cursor-pointer hover:text-neutral-60"
                onClick={onClearHandler}
                data-cy="select-clear-icon"
              />
            )}
            <ChevronDownIcon
              onClick={() => setIsOpened(!isOpened)}
              className={cn(
                'text-neutral-40 transition-transform duration-[0.2s] ease-[ease-in-out] rotate-0',
                isOpened && '-rotate-180!'
              )}
            />
          </div>
        )}
        <AnimatePresence>
          {isOpened && !!options?.length && (
            <m.div
              initial={{ opacity: 0, y: -4 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -4 }}
              transition={{ duration: 0.15, ease: 'easeOut' }}
              className="absolute right-0 left-0 top-[110%] z-30 w-full shadow-xl bg-white border rounded-lg border-solid border-neutral-20"
            >
              {enableSearchField && (
                <div className="p-2 border-b border-neutral-20">
                  <InputSearch
                    ref={searchInputRef}
                    value={dropdownSearchTerm}
                    onChange={(e) => setDropdownSearchTerm(e.target.value)}
                    placeholder="Search..."
                    showSearchIcon={true}
                    onReset={() => setDropdownSearchTerm('')}
                    name={`${cy}-search`}
                    cy={`${cy}-search`}
                  />
                </div>
              )}
              <GenericOption
                className="max-h-[250px] overflow-auto"
                cy={cy}
                options={filteredOptions || []}
                isOpened={isOpened}
                setIsOpened={setIsOpened}
                onSelectOption={onSelectOptionHandler}
              />
            </m.div>
          )}
        </AnimatePresence>
      </div>
    )
  }
)

const Select = memo(
  forwardRef<HTMLInputElement, SelectInputProps>((props, ref) => (
    <GenericSelect {...props} readOnly ref={ref} />
  ))
)

const Autocomplete = memo(
  forwardRef<HTMLInputElement, SelectInputProps>((props, ref) => (
    <GenericSelect {...props} ref={ref} />
  ))
)

const SearchInput = memo(
  forwardRef<HTMLInputElement, SelectInputProps>((props, ref) => (
    <GenericSelect {...props} inputType="search" ref={ref} />
  ))
)

const SelectWithSearch = memo(
  forwardRef<HTMLInputElement, SelectInputProps>((props, ref) => {
    return (
      <GenericSelect
        {...props}
        enableSearchField={props.enableSearchField ?? false}
        readOnly={true}
        ref={ref}
      />
    )
  })
)

export { Select, Autocomplete, SearchInput, SelectWithSearch }
export type { GenericSelectProps }
